import React, { useState, useEffect, useRef } from 'react';
import { soundManager } from '../../utils/soundManager';
import './Hero.css';

const Hero = () => {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const heroRef = useRef(null);
  const watchRef = useRef(null);

  // Specs that appear progressively
  const specs = [
    { label: 'Movement', value: 'Swiss Automatic Tourbillon', icon: '⚙' },
    { label: 'Case', value: '18K Rose Gold, 45mm', icon: '◆' },
    { label: 'Crystal', value: 'Sapphire Anti-Reflective', icon: '◇' },
    { label: 'Water Resistance', value: '100 Meters', icon: '○' },
    { label: 'Power Reserve', value: '72 Hours', icon: '◈' }
  ];

  // Handle scroll for watch rotation and specs reveal
  useEffect(() => {
    const handleScroll = () => {
      if (heroRef.current) {
        const rect = heroRef.current.getBoundingClientRect();
        const heroHeight = rect.height;
        const scrolled = -rect.top;
        
        // Calculate progress (0 to 1)
        const progress = Math.max(0, Math.min(1, scrolled / (heroHeight * 0.8)));
        setScrollProgress(progress);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Intersection observer for entrance
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (heroRef.current) {
      observer.observe(heroRef.current);
    }

    return () => {
      if (heroRef.current) {
        observer.unobserve(heroRef.current);
      }
    };
  }, []);

  const handleExploreClick = () => {
    soundManager.play('ui-click', 0.4);
    const collectionsSection = document.getElementById('collections');
    if (collectionsSection) {
      collectionsSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Calculate watch rotation based on scroll (180 degrees)
  const watchRotation = scrollProgress * 180;

  // Determine which specs should be visible
  const specsVisible = scrollProgress > 0.2;

  return (
    <section 
      id="hero" 
      ref={heroRef} 
      className={`hero-section ${isVisible ? 'visible' : ''}`}
    >
      {/* Background gradient */}
      <div className="hero-background"></div>

      <div className="hero-container">
        {/* Left side - Watch Image */}
        <div className="hero-watch-side">
          <div 
            ref={watchRef}
            className="hero-watch-container"
            style={{
              transform: `rotateY(${watchRotation}deg)`
            }}
          >
            <img 
              src="/images/hero/royal-tourbillon.png" 
              alt="CHRONOS Royal Tourbillon"
              className="hero-watch-image"
            />
            {/* Reflection effect */}
            <div className="watch-reflection"></div>
          </div>

          {/* Scroll indicator */}
          <div className="scroll-indicator">
            <div className="scroll-line"></div>
          </div>
        </div>

        {/* Right side - Content */}
        <div className="hero-content-side">
          <div className="hero-content">
            {/* Collection label */}
            <div className="hero-label">
              <span className="label-line"></span>
              <span className="label-text">Royal Collection</span>
            </div>

            {/* Product name */}
            <h1 className="hero-title">
              <span className="title-main">Royal Tourbillon</span>
              <span className="title-sub">Limited Edition</span>
            </h1>

            {/* Description */}
            <p className="hero-description">
              A masterpiece of horological artistry. The exposed tourbillon 
              complication defies gravity while maintaining precision to 
              within seconds per day. Crafted in 18K rose gold with over 
              400 hours of handwork by Swiss master watchmakers.
            </p>

            {/* Specifications */}
            <div className={`hero-specs ${specsVisible ? 'visible' : ''}`}>
              {specs.map((spec, index) => (
                <div 
                  key={index}
                  className="spec-item"
                  style={{
                    transitionDelay: `${index * 0.1}s`
                  }}
                >
                  <span className="spec-icon">{spec.icon}</span>
                  <div className="spec-content">
                    <span className="spec-label">{spec.label}</span>
                    <span className="spec-value">{spec.value}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Price and CTA */}
            <div className="hero-footer">
              <div className="hero-price">
                <span className="price-label">Starting at</span>
                <span className="price-value">$48,500</span>
              </div>

              <button 
                className="hero-cta"
                onClick={handleExploreClick}
              >
                Explore Collection
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;